import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiX, FiAlertCircle, FiCalendar, FiCreditCard, FiDollarSign, FiRefreshCw, FiFileText } from 'react-icons/fi';
import { format } from 'date-fns';
import api from '../api';
import { useToast } from '../contexts/ToastContext';

export default function SubscriptionManage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/subscriptions/status');

      if (res.data.hasSubscription) {
        setSubscription(res.data.subscription);
        setCurrentPlan(res.data.currentPlan);
      }
    } catch (error) {
      console.error('Fetch subscription error:', error);
      toast.error('구독 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCanceling(true);
      await api.post('/api/subscriptions/cancel', {
        reason: '사용자 요청',
      });

      toast.success('구독이 취소되었습니다.');
      setShowCancelModal(false);
      fetchSubscriptionStatus();
    } catch (error) {
      console.error('Cancel subscription error:', error);
      toast.error(error.response?.data?.error || '구독 취소 중 오류가 발생했습니다.');
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivate = async () => {
    if (!window.confirm('구독을 재활성화하시겠습니까?')) {
      return;
    }

    try {
      await api.post('/api/subscriptions/reactivate');
      toast.success('구독이 재활성화되었습니다.');
      fetchSubscriptionStatus();
    } catch (error) {
      console.error('Reactivate subscription error:', error);
      toast.error(error.response?.data?.error || '구독 재활성화 중 오류가 발생했습니다.');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      ACTIVE: { text: '활성', color: '#10B981', icon: FiCheck },
      SUSPENDED: { text: '정지됨', color: '#EF4444', icon: FiX },
      CANCELED: { text: '취소됨', color: '#6B7280', icon: FiX },
      EXPIRED: { text: '만료됨', color: '#F59E0B', icon: FiAlertCircle },
    };

    const badge = badges[status] || badges.ACTIVE;
    const Icon = badge.icon;

    return (
      <span className="status-badge" style={{ backgroundColor: badge.color }}>
        <Icon size={14} />
        {badge.text}
      </span>
    );
  };

  const getPlanName = (planType) => {
    return planType === 'basic' ? 'Basic' : planType === 'pro' ? 'Pro' : 'Free';
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>구독 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>구독 관리</h1>
        </div>

        <div className="empty-state">
          <FiAlertCircle size={48} color="#9CA3AF" />
          <h3>활성화된 구독이 없습니다</h3>
          <p>Basic 또는 Pro 플랜을 구독하여 더 많은 기능을 이용하세요</p>
          <button className="btn-primary" onClick={() => navigate('/subscription/plans')}>
            플랜 구독하기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>구독 관리</h1>
        <p>현재 구독 중인 플랜 정보와 결제 내역을 확인하세요</p>
      </div>

      {/* 구독 상태 카드 */}
      <div className="subscription-card">
        <div className="card-header">
          <div>
            <h2>{getPlanName(subscription.planType)} 플랜</h2>
            <p className="plan-amount">{subscription.planAmount.toLocaleString()}원/월</p>
          </div>
          {getStatusBadge(subscription.status)}
        </div>

        <div className="card-body">
          <div className="info-row">
            <span className="label">
              <FiCalendar />
              구독 시작일
            </span>
            <span className="value">
              {format(new Date(subscription.startDate), 'yyyy년 MM월 dd일')}
            </span>
          </div>

          {subscription.status === 'ACTIVE' && (
            <div className="info-row">
              <span className="label">
                <FiCalendar />
                다음 결제일
              </span>
              <span className="value highlight">
                {format(new Date(subscription.nextBillingDate), 'yyyy년 MM월 dd일')}
              </span>
            </div>
          )}

          <div className="info-row">
            <span className="label">
              <FiCreditCard />
              결제 수단
            </span>
            <span className="value">
              {subscription.card?.company || '등록된 카드'}
              {subscription.card?.number && ` (${subscription.card.number})`}
            </span>
          </div>

          {subscription.lastPaymentDate && (
            <div className="info-row">
              <span className="label">
                <FiDollarSign />
                최근 결제
              </span>
              <span className="value">
                {format(new Date(subscription.lastPaymentDate), 'yyyy년 MM월 dd일')}
              </span>
            </div>
          )}

          {subscription.failureCount > 0 && subscription.status === 'ACTIVE' && (
            <div className="warning-box">
              <FiAlertCircle />
              <div>
                <strong>결제 실패 경고</strong>
                <p>결제가 {subscription.failureCount}회 실패했습니다. 3회 실패 시 구독이 자동으로 정지됩니다.</p>
                {subscription.lastFailureReason && (
                  <p className="failure-reason">사유: {subscription.lastFailureReason}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="card-actions">
          <button
            className="btn-secondary"
            onClick={() => navigate('/subscription/payments')}
          >
            <FiFileText />
            결제 내역
          </button>

          {subscription.status === 'ACTIVE' && (
            <button
              className="btn-danger"
              onClick={() => setShowCancelModal(true)}
            >
              구독 취소
            </button>
          )}

          {subscription.status === 'SUSPENDED' && (
            <button
              className="btn-primary"
              onClick={handleReactivate}
            >
              <FiRefreshCw />
              구독 재활성화
            </button>
          )}

          {subscription.status === 'CANCELED' && (
            <button
              className="btn-primary"
              onClick={() => navigate('/subscription/plans')}
            >
              새로운 플랜 구독하기
            </button>
          )}
        </div>
      </div>

      {/* 플랜 혜택 안내 */}
      {subscription.status === 'ACTIVE' && (
        <div className="benefits-card">
          <h3>현재 이용 가능한 혜택</h3>
          <div className="benefits-grid">
            <div className="benefit-item">
              <FiCheck className="benefit-icon" />
              <div>
                <h4>학생 수 제한 없음</h4>
                <p>무제한으로 학생을 등록할 수 있습니다</p>
              </div>
            </div>

            {subscription.planType === 'basic' && (
              <div className="benefit-item">
                <FiCheck className="benefit-icon" />
                <div>
                  <h4>매월 10,000P 지급</h4>
                  <p>알림톡 발송에 사용할 수 있는 포인트</p>
                </div>
              </div>
            )}

            {subscription.planType === 'pro' && (
              <div className="benefit-item">
                <FiCheck className="benefit-icon" />
                <div>
                  <h4>쿼터 무제한</h4>
                  <p>모든 기능을 제한 없이 사용</p>
                </div>
              </div>
            )}

            <div className="benefit-item">
              <FiCheck className="benefit-icon" />
              <div>
                <h4>출결 및 성적 관리</h4>
                <p>체계적인 학생 관리 시스템</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 취소 확인 모달 */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>구독 취소 확인</h3>
            <p>정말로 구독을 취소하시겠습니까?</p>
            <div className="modal-warning">
              <FiAlertCircle />
              <div>
                <p><strong>취소 시 변경사항:</strong></p>
                <ul>
                  <li>Free 플랜으로 변경됩니다</li>
                  <li>학생 수가 30명으로 제한됩니다</li>
                  {subscription.planType === 'basic' && (
                    <li>월간 포인트 지급이 중단됩니다</li>
                  )}
                  <li>다음 결제일에 자동 결제가 중단됩니다</li>
                </ul>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowCancelModal(false)}
              >
                돌아가기
              </button>
              <button
                className="btn-danger"
                onClick={handleCancelSubscription}
                disabled={canceling}
              >
                {canceling ? '취소 중...' : '구독 취소'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .subscription-card {
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 24px;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          background: linear-gradient(135deg, #4F46E5, #7C3AED);
          color: white;
        }

        .card-header h2 {
          margin: 0 0 8px 0;
          font-size: 24px;
        }

        .plan-amount {
          margin: 0;
          font-size: 18px;
          opacity: 0.9;
        }

        .status-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 20px;
          color: white;
          font-size: 14px;
          font-weight: 600;
        }

        .card-body {
          padding: 24px;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          border-bottom: 1px solid #F3F4F6;
        }

        .info-row:last-child {
          border-bottom: none;
        }

        .info-row .label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6B7280;
          font-size: 14px;
        }

        .info-row .value {
          font-weight: 600;
          color: #111827;
        }

        .info-row .value.highlight {
          color: #4F46E5;
        }

        .warning-box {
          display: flex;
          gap: 12px;
          padding: 16px;
          background: #FEF2F2;
          border: 1px solid #FCA5A5;
          border-radius: 8px;
          margin-top: 16px;
          color: #991B1B;
        }

        .warning-box svg {
          flex-shrink: 0;
          margin-top: 2px;
        }

        .warning-box strong {
          display: block;
          margin-bottom: 4px;
        }

        .warning-box p {
          margin: 4px 0;
          font-size: 13px;
        }

        .failure-reason {
          font-style: italic;
          opacity: 0.8;
        }

        .card-actions {
          padding: 24px;
          border-top: 1px solid #E5E7EB;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .benefits-card {
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 24px;
        }

        .benefits-card h3 {
          margin: 0 0 20px 0;
          font-size: 18px;
          color: #111827;
        }

        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 16px;
        }

        .benefit-item {
          display: flex;
          gap: 12px;
          padding: 16px;
          background: #F9FAFB;
          border-radius: 8px;
        }

        .benefit-icon {
          color: #10B981;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .benefit-item h4 {
          margin: 0 0 4px 0;
          font-size: 14px;
          color: #111827;
        }

        .benefit-item p {
          margin: 0;
          font-size: 13px;
          color: #6B7280;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          text-align: center;
        }

        .empty-state h3 {
          margin: 16px 0 8px 0;
          color: #111827;
        }

        .empty-state p {
          margin: 0 0 24px 0;
          color: #6B7280;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          padding: 32px;
          max-width: 500px;
          width: 90%;
        }

        .modal-content h3 {
          margin: 0 0 16px 0;
          font-size: 20px;
          color: #111827;
        }

        .modal-content > p {
          margin: 0 0 20px 0;
          color: #6B7280;
        }

        .modal-warning {
          display: flex;
          gap: 12px;
          padding: 16px;
          background: #FEF2F2;
          border: 1px solid #FCA5A5;
          border-radius: 8px;
          margin-bottom: 24px;
          color: #991B1B;
        }

        .modal-warning svg {
          flex-shrink: 0;
          margin-top: 2px;
        }

        .modal-warning p {
          margin: 0 0 8px 0;
          font-size: 14px;
        }

        .modal-warning ul {
          margin: 8px 0 0 0;
          padding-left: 20px;
          font-size: 13px;
        }

        .modal-warning li {
          margin: 4px 0;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .btn-danger {
          background: #EF4444;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          transition: background 0.2s;
        }

        .btn-danger:hover {
          background: #DC2626;
        }

        .btn-danger:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}
